/**
 * Wall-clock projections of a UTC instant into an IANA timezone, shared by the
 * staff-availability slot math, the guest-stay phase logic, the guest-registry
 * export, and push payloads.
 *
 * Timezones arrive as stored free text, so every helper tolerates a missing or
 * invalid zone by falling back to UTC: a defensible UTC wall-clock beats a
 * crash or a silently missing value. UTC-based calendar-day arithmetic (no
 * timezone involved) lives in `iso-date.ts`.
 */

import { Temporal } from "temporal-polyfill";

const HHMM_RE = /^(?<hours>\d{2}):(?<minutes>\d{2})$/u;

/**
 * Split a 24-hour "HH:MM" wall-clock string into its parts, or null when it
 * isn't one. Times reach us as stored free text (a host-entered check-in time,
 * a guest-picked arrival slot), so callers need to tell a real time from a
 * typo without throwing.
 */
export function parseLocalTime(
  hhmm: string | null | undefined
): { hours: number; minutes: number } | null {
  const groups = hhmm ? HHMM_RE.exec(hhmm)?.groups : undefined;
  if (!groups) {
    return null;
  }
  const hours = Number(groups.hours);
  const minutes = Number(groups.minutes);
  return hours > 23 || minutes > 59 ? null : { hours, minutes };
}

// IANA's Area/Location shape, or UTC. Intl also takes offsets ("+01:00",
// which Postgres reads with the sign inverted) and ICU's own IDs ("BST" is
// Asia/Dhaka, "IST" Asia/Calcutta), which are no host's zone.
const IANA_ZONE_NAME_RE = /^(?:UTC|(?!SystemV\/)[A-Z]+(?:\/[\w+-]+)+)$/i;

/**
 * `zone` as an IANA name this runtime can project into, its case corrected
 * where ICU spells the same name, or null when it is not one.
 */
export function ianaTimeZone(zone: string): string | null {
  if (!IANA_ZONE_NAME_RE.test(zone)) {
    return null;
  }
  try {
    // ICU resolves some current names to retired ones (Europe/Kyiv to
    // Europe/Kiev), so its spelling is taken only for the same name.
    const resolved = new Intl.DateTimeFormat("en", {
      timeZone: zone,
    }).resolvedOptions().timeZone;
    return resolved.toLowerCase() === zone.toLowerCase() ? resolved : zone;
  } catch {
    return null;
  }
}

// Constructing an Intl.DateTimeFormat costs far more than formatting with one,
// and list views format per row. Every cached formatter names its zone, so
// none goes stale when the device changes zone. An invalid zone caches its UTC fallback, or each call would throw again.
const formatters = new Map<string, Intl.DateTimeFormat>();

function buildFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
  timeZone: string | null | undefined
): Intl.DateTimeFormat {
  const zone = timeZone || "UTC";
  const key = `${locale}|${zone}|${JSON.stringify(options)}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat(locale, {
        ...options,
        timeZone: zone,
      });
    } catch {
      formatter = new Intl.DateTimeFormat(locale, {
        ...options,
        timeZone: "UTC",
      });
    }
    formatters.set(key, formatter);
  }
  return formatter;
}

/** Calendar date ('YYYY-MM-DD') of the instant in the given timezone. */
export function localDateInTimezone(
  instant: Date | string,
  timeZone: string | null | undefined
): string {
  const d = typeof instant === "string" ? new Date(instant) : instant;
  // en-CA renders ISO-style 'YYYY-MM-DD'.
  return buildFormatter(
    "en-CA",
    { day: "2-digit", month: "2-digit", year: "numeric" },
    timeZone
  ).format(d);
}

/** Wall-clock time ('HH:mm', 24-hour) of the instant in the given timezone. */
export function localTimeInTimezone(
  instant: Date | string,
  timeZone: string | null | undefined
): string {
  const d = typeof instant === "string" ? new Date(instant) : instant;
  // hourCycle "h23" renders midnight as "00", never the "24:xx" that ICU can
  // emit for `hour12: false` (which maps to "h24" on some locale data).
  return buildFormatter(
    "en-GB",
    { hour: "2-digit", hourCycle: "h23", minute: "2-digit" },
    timeZone
  ).format(d);
}

/**
 * The instant `timeZone` reads as `wallClock`, in UTC when the zone is missing
 * or unreadable, as the formatters above fall back. A wall clock the clocks
 * skip resolves forward and a repeated one to its first instant (Temporal's
 * `compatible` disambiguation), so a 02:30 on a clocks-change night still
 * lands on the day it names.
 */
function zonedInstant(
  wallClock: Temporal.PlainDateTime,
  timeZone: string | null | undefined
): Date {
  let zoned: Temporal.ZonedDateTime;
  try {
    zoned = wallClock.toZonedDateTime(timeZone || "UTC");
  } catch {
    zoned = wallClock.toZonedDateTime("UTC");
  }
  return new Date(zoned.epochMilliseconds);
}

/**
 * The instant at which `timeZone` reads the wall clock `dateTime`
 * ("YYYY-MM-DDTHH:mm[:ss]"), or null when it is not one.
 */
export function instantAtWallClock(
  dateTime: string,
  timeZone: string | null | undefined
): Date | null {
  let wallClock: Temporal.PlainDateTime;
  try {
    wallClock = Temporal.PlainDateTime.from(dateTime);
  } catch {
    return null;
  }
  return zonedInstant(wallClock, timeZone);
}

/**
 * The instant a wire timestamp names, or null when it names none. A string
 * with no UTC offset is a wall clock rather than an instant, and reading one
 * in whatever zone the process runs in is the bug this refuses.
 */
export function parseInstant(wire: string): Date | null {
  try {
    return new Date(Temporal.Instant.from(wire).epochMilliseconds);
  } catch {
    return null;
  }
}

/**
 * Carry an instant of a stay to the stay's new check-in day: the same wall
 * clock at the property, as many days after check-in as before. A 15:00
 * arrival stays 15:00 across a clocks change, and a window past midnight
 * stays past midnight. `from` and `to` are check-in day columns.
 */
export function moveWithStayDate(
  instant: Date,
  from: Date,
  to: Date,
  timeZone: string | null | undefined
): Date {
  const sinceCheckIn =
    Date.parse(localDateInTimezone(instant, timeZone)) - from.getTime();
  return withLocalTimeOnStayDate(
    new Date(to.getTime() + sinceCheckIn),
    timeZone,
    localTimeInTimezone(instant, timeZone)
  );
}

/**
 * Place a wall-clock time on the calendar day a STAY belongs to.
 *
 * `check_in_at` and `check_out_at` carry a date the whole system treats as a
 * calendar day and store it as midnight UTC, so anywhere west of Greenwich
 * their LOCAL date is the previous evening. Anchoring there would put a
 * property's 15:00 check-in on the day before the guest arrives.
 *
 * The UTC date is therefore the anchor, and the wall clock is placed on it
 * directly. It must not go through an intermediate instant: no instant is
 * safely "on" a UTC date in every zone (noon UTC is already tomorrow at
 * UTC+12 and beyond), so reading one back through a local date would slip a
 * New Zealand check-in to the day after the guest arrives.
 */
export function withLocalTimeOnStayDate(
  instant: Date | string,
  timeZone: string | null | undefined,
  hhmm: string
): Date {
  const source = typeof instant === "string" ? new Date(instant) : instant;
  const parsed = parseLocalTime(hhmm);
  if (!parsed || Number.isNaN(source.getTime())) {
    return source;
  }
  const day = Temporal.PlainDate.from({
    day: source.getUTCDate(),
    month: source.getUTCMonth() + 1,
    year: source.getUTCFullYear(),
  });
  return zonedInstant(
    day.toPlainDateTime({ hour: parsed.hours, minute: parsed.minutes }),
    timeZone
  );
}
